import { Link, Outlet, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import {
    ArchiveBoxXMarkIcon,
    CameraIcon,
    ChevronLeftIcon,
    PencilSquareIcon,
    RectangleStackIcon,
    ShareIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import {
    Alert,
    AlertActions,
    AlertDescription,
    AlertTitle,
    BadgeButton,
    Button,
    EmptyState,
    Tab,
    TabLayout,
    TabMenu,
    notify,
} from "components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "src/api/api";
import { CollectionDialog } from "src/components/collection-dialog";
import { RequireAccount } from "src/components/require-account";
import { useScannerSessions } from "src/context/scanner-session-context";
import { ShareDialog } from "src/components/share-dialog";
import i18n from "src/i18n";
import { useShortcuts } from "src/utils/use-shortcuts";
import { useShortcutHelpOpen } from "src/context/shortcut-help-context";

/** How the mini buttons above the tabs are framed */
const ACTION_RING = "ring-1 ring-zinc-950/10 dark:ring-white/15";

export const Route = createFileRoute("/_menu/collections/$collectionUuid/_collection")({
    // Only the collection itself, which is what the chrome around the tabs
    // needs. The stacks used to be loaded here as well, so that both tabs could
    // share them — but that meant every visit pulled the whole collection, five
    // figures of rows, before anything could be drawn. Each tab now asks for
    // what it actually shows: the card list for one page, the statistics for
    // the lot.
    loader: async ({ params }) => {
        const [collection, tags] = await Promise.all([
            Api.collections.getIfThere(params.collectionUuid),
            Api.tags.list(),
            i18n.loadNamespaces("deck"),
        ]);
        return { collection, tags: tags.tags };
    },
    component: RouteComponent,
});

/**
 * The chrome around one collection: its name and the tabs holding cards and numbers.
 *
 * Pathless, so the cards stay at `/collections/{uuid}` and every existing link
 * to a collection keeps working.
 *
 * @returns the tabbed frame around the current tab
 */
function RouteComponent() {
    const { collectionUuid } = Route.useParams();
    const { collection } = Route.useLoaderData();
    const [t] = useTranslation("collection");
    const [tg] = useTranslation();
    const router = useRouter();
    const navigate = useNavigate();
    const [sharing, setSharing] = useState(false);
    const [editing, setEditing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const sessions = useScannerSessions();

    const deckUuid = collection?.deck ?? null;
    const shortcutHelpOpen = useShortcutHelpOpen();

    const tabs = [
        "/collections/$collectionUuid/cards",
        "/collections/$collectionUuid/on-loan",
        "/collections/$collectionUuid/statistics",
    ] as const;

    useShortcuts(
        {
            ...Object.fromEntries(
                tabs.map((tab, index) => [
                    String(index + 1),
                    () => void navigate({ to: tab, params: { collectionUuid } }),
                ]),
            ),
            e: () => {
                if (deckUuid === null) setEditing(true);
            },
            s: () => {
                if (deckUuid === null) setSharing(true);
            },
        },
        !sharing && !editing && !confirming && !shortcutHelpOpen,
    );

    /**
     * Deletes the collection and leaves for the list it was in
     */
    async function remove() {
        setConfirming(false);
        await Api.collections.delete(collectionUuid);
        notify.success(t("toast.collection-deleted"));
        await navigate({ to: "/collections" });
    }

    if (collection === null) {
        return (
            <RequireAccount>
                <EmptyState
                    icon={<ArchiveBoxXMarkIcon />}
                    title={t("heading.collection-gone")}
                    description={t("description.collection-gone")}
                    action={
                        <Button outline={true} href={"/collections"}>
                            {t("button.back-to-collections")}
                        </Button>
                    }
                />
            </RequireAccount>
        );
    }

    return (
        <RequireAccount>
            <div className={"flex flex-col gap-2"}>
                <Link
                    to={"/collections"}
                    className={
                        "flex items-center gap-1 text-sm text-zinc-500 hover:text-(--color-accent) hover:underline dark:text-zinc-400"
                    }
                >
                    <ChevronLeftIcon className={"size-4"} /> {t("button.back-to-collections")}
                </Link>
                <TabLayout
                    heading={collection.name}
                    headingDescription={
                        <span className={"flex flex-col gap-3"}>
                            {collection.description !== "" && <span>{collection.description}</span>}
                            <span className={"flex flex-wrap items-center gap-2"}>
                                {/* A scan session on this box. Everything the scanner then stages
                                    is offered back to this collection, so sorting a box is one
                                    walk: open the box, scan the stack, file it, keep going. Deck
                                    collections get it too — a deck's cards are cards in a box like
                                    any other, and they are the ones most often counted again. */}
                                <BadgeButton
                                    color={"zinc"}
                                    className={ACTION_RING}
                                    onClick={() => {
                                        void sessions.aimAt(collectionUuid, collection.name).then(
                                            () => navigate({ to: "/scan" }),
                                            // The scanner is still worth opening when the session
                                            // could not be aimed — it stages either way, and the
                                            // filing button asks where.
                                            () => navigate({ to: "/scan" }),
                                        );
                                    }}
                                >
                                    <CameraIcon className={"size-3.5"} />
                                    {t("button.scan-into")}
                                </BadgeButton>
                                {deckUuid !== null ? (
                                    <BadgeButton
                                        color={"zinc"}
                                        className={ACTION_RING}
                                        href={"/decks/$deckUuid/cards"}
                                        params={{ deckUuid }}
                                    >
                                        <RectangleStackIcon className={"size-3.5"} />
                                        {t("button.open-deck")}
                                    </BadgeButton>
                                ) : (
                                    <>
                                        <BadgeButton
                                            color={"zinc"}
                                            className={ACTION_RING}
                                            onClick={() => setSharing(true)}
                                        >
                                            <ShareIcon className={"size-3.5"} />
                                            {t("button.share-collection")}
                                        </BadgeButton>
                                        <BadgeButton
                                            color={"zinc"}
                                            className={ACTION_RING}
                                            onClick={() => setEditing(true)}
                                        >
                                            <PencilSquareIcon className={"size-3.5"} />
                                            {t("button.edit-collection")}
                                        </BadgeButton>
                                        <BadgeButton
                                            color={"zinc"}
                                            className={ACTION_RING}
                                            onClick={() => setConfirming(true)}
                                        >
                                            <TrashIcon className={"size-3.5"} />
                                            {t("button.delete-collection")}
                                        </BadgeButton>
                                    </>
                                )}
                            </span>
                        </span>
                    }
                    tabs={
                        <TabMenu>
                            <Tab href={"/collections/$collectionUuid/cards"} params={{ collectionUuid }}>
                                {t("heading.cards")}
                            </Tab>
                            <Tab href={"/collections/$collectionUuid/on-loan"} params={{ collectionUuid }}>
                                {t("label.on-loan")}
                            </Tab>
                            <Tab href={"/collections/$collectionUuid/statistics"} params={{ collectionUuid }}>
                                {t("heading.statistics")}
                            </Tab>
                        </TabMenu>
                    }
                >
                    <Outlet />
                </TabLayout>

                <ShareDialog
                    subject={
                        sharing
                            ? {
                                  kind: "collections",
                                  uuid: collection.uuid,
                                  visibility: collection.visibility,
                                  shareToken: collection.share_token ?? null,
                                  owner: true,
                              }
                            : null
                    }
                    linkDescription={t("description.share-link")}
                    onClose={() => setSharing(false)}
                    onChanged={() => router.invalidate()}
                />

                <CollectionDialog
                    open={editing}
                    collection={collection}
                    onClose={() => setEditing(false)}
                    onSaved={() => {
                        setEditing(false);
                        notify.success(t("toast.collection-updated"));
                        void router.invalidate();
                    }}
                />

                <Alert open={confirming} onClose={() => setConfirming(false)}>
                    <AlertTitle>{t("heading.delete-collection")}</AlertTitle>
                    <AlertDescription>{t("description.delete-collection", { name: collection.name })}</AlertDescription>
                    <AlertActions>
                        <Button plain onClick={() => setConfirming(false)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button color={"red"} onClick={() => void remove()}>
                            {t("button.delete-collection")}
                        </Button>
                    </AlertActions>
                </Alert>
            </div>
        </RequireAccount>
    );
}
